import { useParams, Link } from 'react-router-dom';
import { manhwaList, chapters } from '@/mocks/manhwa';
import { useSchemaOrg } from '@/hooks/useSchemaOrg';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const manhwa = manhwaList.find((m) => m.slug === slug);
  const related = manhwaList
    .filter((m) => m.category === manhwa?.category && m.slug !== slug)
    .slice(0, 4);

  const articleSchema = manhwa ? {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: manhwa.title,
    author: {
      '@type': 'Person',
      name: manhwa.author,
    },
    datePublished: '2026-01-15',
    dateModified: manhwa.updatedAt,
    description: manhwa.description,
    inLanguage: 'zh-CN',
    isPartOf: {
      '@type': 'WebSite',
      name: '韩漫网站',
      url: 'https://www.example.com',
    },
  } : null;

  const breadcrumbSchema = manhwa ? {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://www.example.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: manhwa.categoryName,
        item: `https://www.example.com/category/${manhwa.category}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: manhwa.title,
      },
    ],
  } : null;

  useSchemaOrg(articleSchema);
  useSchemaOrg(breadcrumbSchema);

  if (!manhwa) {
    return (
      <div className="min-h-screen bg-background-50 pt-16 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-200 mb-4">
            <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
          </div>
          <h2 className="text-xl font-heading font-bold text-foreground-900 mb-2">漫画未找到</h2>
          <p className="text-sm text-foreground-500 mb-6">抱歉，该漫画不存在或已被移除</p>
          <Link
            to="/"
            className="inline-flex px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50 pt-16">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8 flex-wrap">
          <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">首页</Link>
          <span>/</span>
          <Link to={`/category/${manhwa.category}`} className="hover:text-primary-600 transition-colors whitespace-nowrap">
            {manhwa.categoryName}
          </Link>
          <span>/</span>
          <span className="text-foreground-800 font-medium">{manhwa.title}</span>
        </nav>

        <article className="flex flex-col lg:flex-row gap-8 mb-16">
          <div className="w-full lg:w-72 flex-shrink-0">
            <div className="aspect-[3/4] rounded-xl overflow-hidden bg-background-100 sticky top-24">
              <img
                src={manhwa.cover}
                alt={manhwa.title}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div className="flex-1">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">
              {manhwa.title}
            </h1>

            <div className="flex items-center gap-4 flex-wrap mb-6">
              <span className="text-sm text-foreground-500">作者：<strong className="text-foreground-800">{manhwa.author}</strong></span>
              <span className="text-foreground-300">|</span>
              <Link
                to={`/category/${manhwa.category}`}
                className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium hover:bg-accent-200 transition-colors whitespace-nowrap cursor-pointer"
              >
                {manhwa.categoryName}
              </Link>
              <span className="text-foreground-300">|</span>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                manhwa.status === '连载中' ? 'bg-secondary-100 text-secondary-700' : 'bg-primary-100 text-primary-700'
              }`}>
                {manhwa.status}
              </span>
            </div>

            <div className="flex items-center gap-6 mb-6">
              <div className="flex items-center gap-1.5">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-star-fill text-amber-400"></i>
                </div>
                <span className="text-lg font-bold text-foreground-900">{manhwa.rating}</span>
                <span className="text-xs text-foreground-500">/ 5.0</span>
              </div>
              <div className="flex items-center gap-1.5 text-sm text-foreground-600">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-eye-line"></i>
                </div>
                <span>{(manhwa.views / 10000).toFixed(1)}万阅读</span>
              </div>
              <div className="flex items-center gap-1.5 text-sm text-foreground-600">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-file-list-3-line"></i>
                </div>
                <span>共 {manhwa.chapters} 话</span>
              </div>
            </div>

            <div className="prose prose-sm max-w-none mb-6">
              <h3 className="text-base font-heading font-semibold text-foreground-900 mb-3">作品简介</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                {manhwa.description}
              </p>
            </div>

            <div className="flex items-center gap-3 mb-8">
              <Link
                to={`/article/${manhwa.slug}`}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-play-circle-line"></i>
                </div>
                开始阅读第一话
              </Link>
              <button className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-primary-600 transition-colors cursor-pointer">
                <i className="ri-bookmark-line text-lg"></i>
              </button>
              <button className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-primary-600 transition-colors cursor-pointer">
                <i className="ri-share-forward-line text-lg"></i>
              </button>
            </div>
          </div>
        </article>

        <section className="mb-16">
          <h2 className="text-xl font-heading font-bold text-foreground-900 mb-6">章节列表</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {chapters.map((chapter) => (
              <Link
                key={chapter.id}
                to={`/article/${manhwa.slug}`}
                className="flex items-center justify-between p-4 rounded-lg bg-background-100 hover:bg-primary-50/50 transition-colors cursor-pointer"
              >
                <span className="text-sm text-foreground-800 font-medium">{chapter.title}</span>
                <span className="text-xs text-foreground-400 whitespace-nowrap">{chapter.updatedAt}</span>
              </Link>
            ))}
          </div>
        </section>

        {related.length > 0 && (
          <section className="mb-16">
            <h2 className="text-xl font-heading font-bold text-foreground-900 mb-6">相关推荐</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {related.map((item) => (
                <Link
                  key={item.id}
                  to={`/article/${item.slug}`}
                  className="group cursor-pointer"
                >
                  <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-3 bg-background-100">
                    <img
                      src={item.cover}
                      alt={item.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                      <div className="flex items-center gap-1">
                        <div className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className="ri-star-fill text-amber-400 text-xs"></i>
                        </div>
                        <span className="text-white text-xs font-medium">{item.rating}</span>
                      </div>
                    </div>
                  </div>
                  <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                    {item.title}
                  </h3>
                  <p className="text-xs text-foreground-500 mt-1">{item.author}</p>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}